import { Link } from "react-router-dom";
import { FileText, Mail, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTASection } from "../components/CTASection";
import { Reveal } from "../components/Reveal";
import { REPORTS, ORG } from "../data/content";

const typeColor = (t) =>
  t === "Annual Report"
    ? "bg-hecso-greensubtle text-hecso-greendark border-emerald-200"
    : t === "Financial"
    ? "bg-hecso-orangelight text-amber-800 border-amber-200"
    : "bg-slate-100 text-slate-700 border-slate-200";

export default function Reports() {
  return (
    <main data-testid="reports-page">
      <PageHeader
        title="Reports & Transparency"
        subtitle="Annual reports, financial statements and program reports documenting our work and accountability."
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="container-hecso">
          <Reveal className="mb-10 flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-emerald-200 bg-hecso-greensubtle p-6">
            <ShieldCheck className="w-10 h-10 shrink-0 text-hecso-green" />
            <div>
              <h2 className="font-heading text-lg font-semibold text-hecso-greendark">Our Commitment to Transparency</h2>
              <p className="mt-1 text-sm text-hecso-muted leading-relaxed">
                Our accounts are audited annually, and reports are shared with donors, partners and government
                stakeholders. Donations are eligible for tax benefits under Section 80G of the Income Tax Act.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REPORTS.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 80}>
                <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md" data-testid={`report-card-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-hecso-beige text-hecso-green">
                    <FileText className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-hecso-ink">{r.title}</h3>
                  <div className="mt-3 flex items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${typeColor(r.type)}`}>{r.type}</span>
                    <span className="text-xs text-hecso-muted">{r.year} · {r.pages}</span>
                  </div>
                  <a
                    href={`mailto:${ORG.email}?subject=Request: ${encodeURIComponent(r.title)}`}
                    data-testid={`report-request-${i}`}
                    className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-hecso-green hover:text-hecso-greendark transition-colors"
                  >
                    <Mail className="w-4 h-4" /> Request a Copy
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10 text-center">
            <p className="text-sm text-hecso-muted">
              Printed and digital copies of all reports are available on request. Write to us at{" "}
              <a href={`mailto:${ORG.email}`} className="font-semibold text-hecso-green hover:underline">{ORG.email}</a>{" "}
              or visit our <Link to="/contact" className="font-semibold text-hecso-green hover:underline">contact page</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
